/**
 * Any environment variant
 */
import { vercel } from '@t3-oss/env-core/presets'
import { createEnv } from '@t3-oss/env-nextjs'
import { z } from 'zod'

export const booleanFromStringSchema = z.enum(['true', 'false']).transform((v) => v === 'true')

/**
 * Environments:
 * - Development (in vercel: global-ka-development / Development)
 * - Testing (in Vercel: global-ka-development / Preview)
 * - Staging (in Vercel: global-ka-development / Production)
 * - Production (in Vercel: global-ka-production / Production)
 * TODO: Shall we change this and use one project instead?
 */

export const env = createEnv({
  extends: [
    // Enables Vercel System Environment Variables
    // https://vercel.com/docs/projects/environment-variables/system-environment-variables#system-environment-variables
    vercel(),
  ],
  /**
   * Specify your server-side environment variables schema here. This way you can ensure the app isn't built with invalid env vars.
   */
  server: {
    DATABASE_URL: z.string().url(),
    DATABASE_URL_UNPOOLED: z.string().url(),

    NODE_ENV: z.enum(['development', 'test', 'production']).default('development'),

    // Auth
    /**
     * AUTH_SECRET is a random token used by the library to encrypt tokens and email verification hashes, and it’s mandatory to keep things secure.
     *
     * Auth.js will automatically pick this variable during initialization.
     *
     * You can generate a new secret on the command line with:
     * $ npx auth secret
     *
     * Each environment must have a different secret.
     */
    AUTH_SECRET: z.string(),

    /**
     * CRON_SECRET is a secure random token used to safely trigger cron jobs.
     */
    CRON_SECRET: z.string(),

    // TODO: Document
    AUTH_URL:
      process.env.NODE_ENV === 'production'
        ? z.preprocess(
            // This makes Vercel deployments not fail if you don't set AUTH_URL
            // Since NextAuth.js automatically uses the VERCEL_URL if present.
            (str) => process.env.VERCEL_URL ?? str,
            // VERCEL_URL doesn't include `https` so it cant be validated as a URL
            process.env.VERCEL ? z.string() : z.string().url(),
          )
        : z.string().optional(),

    /**
     * Auth.js is automatically configured to pick the right environment variables for clientId and clientSecret when using an official OAuth provider.
     *
     * Each environment must have different pairs of keys.
     * Development environment can use Testing variables.
     */
    AUTH_GOOGLE_ID: z.string(),
    AUTH_GOOGLE_SECRET: z.string(),

    /**
     * This environment variable is needed when using Auth.js as a proxy for preview deploys, for example.
     *
     * https://authjs.dev/getting-started/deployment#securing-a-preview-deployment
     *
     * Needed only for the preview environment.
     */
    AUTH_REDIRECT_PROXY_URL: z.string().optional(),

    /**
     * The number of rounds of salting and hashing to apply when generating a bcrypt hash.
     *
     * Salting adds random data to a password before hashing, and the number of rounds affects the complexity of the process. A higher number of salt rounds increases the time it takes to generate and verify a password hash, which makes it more secure against brute-force attacks but can also slow down performance. Commonly, values between 10 and 12 are used, though higher values offer stronger security at the cost of slower computation.
     */
    BCRYPT_SALT_ROUNDS: z.string().regex(/^\d+$/).transform(Number),

    /**
     * Enabling the debug option in production can lead to sensitive information being saved in the logs. So it always defaults to `false` in production.
     */
    DEBUG_NEXT_AUTH:
      process.env.NODE_ENV === 'production'
        ? booleanFromStringSchema.optional().transform(() => false)
        : booleanFromStringSchema,

    // Google
    GOOGLE_API_KEY: z.string(),

    // Google service account
    GOOGLE_SERVICE_PRIVATE_KEY: z.string(),
    GOOGLE_SERVICE_CLIENT_EMAIL: z.string(),
    GOOGLE_SERVICE_SHARED_FOLDER_ID: z.string(),

    // Google drive manual testing, in it fails
    GOOGLE_SERVICE_ERROR_CLASS_PALETTE_CREATION: booleanFromStringSchema,
    GOOGLE_SERVICE_ERROR_INDIVIDUAL_PALETTE_CREATION: booleanFromStringSchema,

    // Vimeo
    VIMEO_ACCESS_TOKEN: z.string(),

    // Emails
    RESEND_API_KEY: z.string(),
    // Single or multiple email addresses, separate with commas (,)
    KA_SUPPORT_EMAIL_ADDRESS: z.string(),

    // AWS
    AWS_ACCESS_KEY_ID: z.string(),
    AWS_SECRET_ACCESS_KEY: z.string(),
    /**
     * Name of the AWS S3 bucket with private content (e.g. Resources and Materials).
     */
    AWS_PRIVATE_BUCKET_NAME: z.string(),
    CLOUDFRONT_KEY_PAIR_ID: z.string(),
    CLOUDFRONT_PRIVATE_KEY: z.string(),

    // Pipedrive
    PIPEDRIVE_API_KEY: z.string(),
    ZAPIER_SECRET_ACCESS_KEY: z.string(),

    // Zoom
    ZOOM_CLIENT_ID: z.string(),
    ZOOM_CLIENT_SECRET: z.string(),
    ZOOM_ACCOUNT_ID: z.string(),
    ZOOM_ACCOUNT_EMAILS: z.enum(['testing', 'production']),
    ZOOM_WEBHOOK_SECRET_TOKEN: z.string(),

    // Logtail
    LOGTAIL_SOURCE_TOKEN: z.string().optional(),
  },

  /**
   * Specify your client-side environment variables schema here. This way you can ensure the app
   * isn't built with invalid env vars. To expose them to the client, prefix them with
   * `NEXT_PUBLIC_`.
   */
  client: {
    NEXT_PUBLIC_ENABLE_GOOGLE_OAUTH: booleanFromStringSchema,

    NEXT_PUBLIC_APP_URL: z.string(),

    // AWS
    /**
     * Name of the AWS S3 region.
     */
    NEXT_PUBLIC_AWS_REGION: z.string(),
    /**
     * Name of the AWS S3 bucket with publicly available content (e.g. Banners and Icons).
     */
    NEXT_PUBLIC_AWS_PUBLIC_BUCKET_NAME: z.string(),
    /**
     * URL of the AWS endpoint used to initialize the client. Should be provided only in development setup with MinIO. Should be undefined when connecting to AWS.
     */
    NEXT_PUBLIC_AWS_ENDPOINT: z.string().optional(),

    // Sentry
    NEXT_PUBLIC_SENTRY_DSN: z.string().optional(),

    // TinyMCE for text editing
    NEXT_PUBLIC_TINYMCE_API_KEY: z.string(),

    NEXT_PUBLIC_ENABLE_CLOUDFRONT: booleanFromStringSchema,
    NEXT_PUBLIC_CLOUDFRONT_DISTRIBUTION_DOMAIN_NAME: z.string(),
  },

  /**
   * You can't destruct `process.env` as a regular object in the Next.js edge runtimes (e.g. middlewares) or client-side so we need to destruct manually.
   */
  experimental__runtimeEnv: {
    NEXT_PUBLIC_ENABLE_GOOGLE_OAUTH: process.env.NEXT_PUBLIC_ENABLE_GOOGLE_OAUTH,
    NEXT_PUBLIC_APP_URL: process.env.NEXT_PUBLIC_APP_URL,
    NEXT_PUBLIC_AWS_REGION: process.env.NEXT_PUBLIC_AWS_REGION,
    NEXT_PUBLIC_AWS_PUBLIC_BUCKET_NAME: process.env.NEXT_PUBLIC_AWS_PUBLIC_BUCKET_NAME,
    NEXT_PUBLIC_AWS_ENDPOINT: process.env.NEXT_PUBLIC_AWS_ENDPOINT,
    NEXT_PUBLIC_SENTRY_DSN: process.env.NEXT_PUBLIC_SENTRY_DSN,
    NEXT_PUBLIC_TINYMCE_API_KEY: process.env.NEXT_PUBLIC_TINYMCE_API_KEY,
    NEXT_PUBLIC_ENABLE_CLOUDFRONT: process.env.NEXT_PUBLIC_ENABLE_CLOUDFRONT,
    NEXT_PUBLIC_CLOUDFRONT_DISTRIBUTION_DOMAIN_NAME:
      process.env.NEXT_PUBLIC_CLOUDFRONT_DISTRIBUTION_DOMAIN_NAME,
  },
  /**
   * Run `build` or `dev` with `SKIP_ENV_VALIDATION` to skip env validation. This is especially useful for Docker builds.
   */
  skipValidation: !!process.env.SKIP_ENV_VALIDATION,
  /**
   * Makes it so that empty strings are treated as undefined. `SOME_VAR: z.string()` and
   * `SOME_VAR=''` will throw an error.
   */
  emptyStringAsUndefined: true,
})
